'use client'

import { useEffect } from 'react'
import { pushGTMEvent } from '@/lib/gtm'

/** 'trackName' -> 'name', 'trackProjectId' -> 'project_id' */
function toParamName(datasetKey: string) {
  const raw = datasetKey.replace(/^track/, '')
  return raw.charAt(0).toLowerCase() + raw.slice(1).replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

/**
 * Medição sem JavaScript espalhado pelos componentes:
 * - Cliques: qualquer elemento com `data-track="nome_do_evento"` envia esse evento ao dataLayer;
 *   os demais `data-track-*` viram parâmetros (ex.: data-track-name="whatsapp" -> name: 'whatsapp').
 * - Seções: envia `section_view` uma única vez quando cada <section id> aparece na tela.
 */
export default function Analytics() {
  useEffect(() => {
    function onClick(event: MouseEvent) {
      const target = event.target
      if (!(target instanceof Element)) return
      const element = target.closest<HTMLElement>('[data-track]')
      if (!element) return

      const params: Record<string, string> = { locale: document.documentElement.lang }
      for (const [key, value] of Object.entries(element.dataset)) {
        if (key !== 'track' && key.startsWith('track') && value !== undefined) params[toParamName(key)] = value
      }
      pushGTMEvent({ event: element.dataset.track as string, ...params })
    }

    document.addEventListener('click', onClick, { capture: true })

    let observer: IntersectionObserver | undefined
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue
            pushGTMEvent({ event: 'section_view', section: entry.target.id, locale: document.documentElement.lang })
            observer?.unobserve(entry.target)
          }
        },
        // Conta a seção quando ela entra nos 60% de cima da tela; assim seções muito altas (como Trabalhos no celular) também são contadas
        { rootMargin: '0px 0px -40% 0px', threshold: 0 },
      )
      document.querySelectorAll('main section[id]').forEach((section) => observer?.observe(section))
    }

    return () => {
      document.removeEventListener('click', onClick, { capture: true })
      observer?.disconnect()
    }
  }, [])

  return null
}
